import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { FaLinkedin, FaFlickr, FaInstagram, FaEnvelope, FaChevronUp, FaReact } from 'react-icons/fa';
import { ScaleIcon } from '@heroicons/react/24/outline';

import { sectionStyles } from '../styles/globalStyles';

const CONTACT_LINKS = [
    {
        id: 'email',
        href: 'mailto:eduardomartinsw@outlook.com',
        IconComponent: FaEnvelope,
        textKey: 'contact.email',
        ariaLabel: 'Email',
        hoverClasses: 'hover:text-lime-500 dark:hover:text-lime-200',
        isExternal: false,
    },
    {
        id: 'linkedin',
        href: 'https://www.linkedin.com/in/eduardormw/',
        IconComponent: FaLinkedin,
        text: 'LinkedIn',
        ariaLabel: 'LinkedIn',
        hoverClasses: 'hover:text-blue-700 dark:hover:text-blue-400',
        isExternal: true,
    },{
        id: 'instagram',
        href: 'https://www.instagram.com/eduardormw',
        IconComponent: FaInstagram,
        text: 'Instagram',
        ariaLabel: 'Instagram',
        hoverClasses: 'hover:text-pink-600 dark:hover:text-pink-400',
        isExternal: true,
    },
    {
        id: 'flickr',
        href: 'https://www.flickr.com/photos/eduardormw/albums',
        IconComponent: FaFlickr,
        text: 'Flickr',
        ariaLabel: 'Flickr',
        hoverClasses: 'hover:text-purple-600 dark:hover:text-purple-400',
        isExternal: true,
    }
];

const Contact = ({ openLegalModal }) => {
    const { t } = useTranslation();

    const scrollToTop = useCallback(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    return (
        <section id="contacts" className={sectionStyles.section}>
            <div className={sectionStyles.container}>
                <div className={sectionStyles.title}>
                    <p className={sectionStyles.breadcrumb}>
                        <ChevronRightIcon className="w-4 h-4 mr-2" />
                        {t('contact.title')}
                    </p>
                    <h2 className={sectionStyles.heading}>
                        {t('contact.subtitle')}
                    </h2>
                </div>
                <p className="text-2xl font-light mb-12">
                    {t('contact.description')}
                </p>

                <div className="grid grid-cols-2 gap-x-8 gap-y-6 px-2 md:flex md:flex-wrap md:gap-8 lg:justify-start">
                    {CONTACT_LINKS.map(link => (
                        <a
                            key={link.id}
                            href={link.href}
                            className={`flex items-center gap-2 ${link.hoverClasses} transition`}
                            aria-label={link.ariaLabel}
                            {...(link.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                            <link.IconComponent className="w-8 h-8" />
                            <span>{link.textKey ? t(link.textKey) : link.text}</span>
                        </a>
                    ))}
                </div>
                <div className="flex justify-center mt-12">
                    <button
                        type="button"
                        onClick={scrollToTop}
                        className="flex flex-col items-center justify-start w-full md:max-w-80 px-4 py-2 cursor-pointer rounded dropdown-toggle"
                        aria-label={t('contact.top_aria_label') || "Back to Top"}
                    >
                        <FaChevronUp className="w-5 h-5" />
                        <span>{t('contact.top')}</span>
                    </button>
                </div>

                <div className="flex flex-col md:flex-row items-center justify-center md:justify-between mt-24 gap-4">
                    <p
                        className="text-text-light dark:text-text-dark opacity-70 hover:opacity-100 dark:hover:opacity-100 hover:text-textHover-light dark:hover:text-textHover-dark cursor-pointer transition-all duration-200"
                        onClick={openLegalModal}
                        aria-label={t("legal.buttons.open") || "Open Privacy Policy and Terms"}
                    >
                        <ScaleIcon className="w-5 h-5 inline mr-1" /> {t("legal.buttons.open")}
                    </p>
                    <div className='flex items-center'>
                        <FaReact className="w-6 h-6 mr-2 text-blue-500" />
                        <p>{t('intro.powered_by')}</p></div>
                </div>
            </div>
        </section>
    );
};

export default Contact;